"use client";

import { useEffect } from "react";
import { useCurrentHint } from "@/contexts/hint-context";
import { useStudio } from "@/contexts/studio-context";

/**
 * Mirror the unified HintContext into the studio piano runtime: whenever
 * the topmost hint changes, push its midi→color map via piano.setHints();
 * clear when nothing is published.
 *
 * Renders nothing, meant to be mounted once inside the studio shell.
 */
export function StudioPianoHintBridge() {
  const { studio } = useStudio();
  const hint = useCurrentHint();

  // Pas de `clearHints()` au nettoyage de cet effet : un hint qui en
  // remplace un autre émettait deux `hints_changed` par onset (vidage puis
  // remplissage). `setHints` remplace d'un coup et sait déjà taire un
  // contenu identique.
  useEffect(() => {
    const piano = studio?.getPiano();

    if (!piano) {
      return;
    }

    if (!hint || hint.midis.length === 0) {
      piano.clearHints();

      return;
    }

    const map = new Map<number, number>();
    for (const note of hint.midis) {
      map.set(note.midi, note.color);
    }

    piano.setHints(map);
  }, [studio, hint]);

  // Le vidage n'a lieu qu'au démontage réel (ou au changement de studio).
  useEffect(() => {
    const piano = studio?.getPiano();

    if (!piano) {
      return;
    }

    return () => {
      piano.clearHints();
    };
  }, [studio]);

  return null;
}
